import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import url from '../constants/config';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const InternalNotes = ({ playerId }) => {
  const { userRole, userEmail } = useSelector(({ authorization }) => authorization);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchNotes = useCallback(() => {
    if (!playerId) return;
    setLoading(true);
    axios
      .get(`${url}/checklist/${playerId}/internal-notes`, { headers: getAuthHeader() })
      .then(res => setNotes(res.data.notes || []))
      .catch(() => setError('Nie udało się załadować notatek.'))
      .finally(() => setLoading(false));
  }, [playerId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post(
        `${url}/checklist/${playerId}/internal-notes`,
        { text: text.trim() },
        { headers: getAuthHeader() }
      );
      setNotes(prev => [res.data.note, ...prev]);
      setText('');
    } catch {
      setError('Nie udało się dodać notatki.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    setDeletingId(noteId);
    try {
      await axios.delete(
        `${url}/checklist/${playerId}/internal-notes/${noteId}`,
        { headers: getAuthHeader() }
      );
      setNotes(prev => prev.filter(n => n._id !== noteId));
    } catch {
      setError('Nie udało się usunąć notatki.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div style={{ maxWidth: '720px' }}>
      {/* Info badge */}
      <div
        style={{
          background: '#fef9c3',
          border: '1px solid #fde68a',
          borderRadius: '8px',
          padding: '10px 14px',
          fontSize: '13px',
          color: '#78350f',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <i className="fas fa-lock" />
        Notatki wewnętrzne — widoczne tylko dla admina i asystentów. Zawodnik ich nie widzi.
      </div>

      {/* Add form */}
      <form onSubmit={handleAdd} style={{ marginBottom: '24px' }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={3}
          placeholder="Dodaj notatkę wewnętrzną... (np. 'Czekamy na dokument od ojca', 'Kontakt przez WhatsApp')"
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            fontSize: '14px',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
            fontFamily: 'inherit',
            lineHeight: 1.5,
          }}
        />
        {error && (
          <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '6px' }}>{error}</div>
        )}
        <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            disabled={saving || !text.trim()}
            style={{
              padding: '8px 20px',
              background: saving || !text.trim() ? '#e2e8f0' : '#2e496b',
              color: saving || !text.trim() ? '#94a3b8' : '#fff',
              border: 'none',
              borderRadius: '6px',
              fontWeight: 600,
              fontSize: '14px',
              cursor: saving || !text.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Zapisywanie...' : '+ Dodaj notatkę'}
          </button>
        </div>
      </form>

      {/* Notes list */}
      {loading ? (
        <div style={{ color: '#94a3b8', fontSize: '14px' }}>Ładowanie notatek...</div>
      ) : notes.length === 0 ? (
        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '32px',
            textAlign: 'center',
            color: '#94a3b8',
            fontSize: '14px',
          }}
        >
          <i className="fas fa-sticky-note" style={{ fontSize: '24px', marginBottom: '8px', display: 'block' }} />
          Brak notatek wewnętrznych dla tego zawodnika.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {notes.map(note => {
            const canDelete = userRole === 'admin' || note.authorEmail === userEmail;
            return (
              <div
                key={note._id}
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '12px',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: '14px',
                        color: '#1e293b',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {note.text}
                    </div>
                    <div
                      style={{
                        marginTop: '8px',
                        fontSize: '12px',
                        color: '#94a3b8',
                        display: 'flex',
                        gap: '12px',
                      }}
                    >
                      <span>
                        <i className="fas fa-user" style={{ marginRight: '4px' }} />
                        {note.authorName || note.authorEmail}
                      </span>
                      <span>
                        <i className="fas fa-clock" style={{ marginRight: '4px' }} />
                        {formatDate(note.createdAt)}
                      </span>
                    </div>
                  </div>
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => handleDelete(note._id)}
                      disabled={deletingId === note._id}
                      title="Usuń notatkę"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#cbd5e1',
                        fontSize: '14px',
                        padding: '2px 4px',
                        flexShrink: 0,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#cbd5e1'; }}
                    >
                      <i className="fas fa-trash-alt" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InternalNotes;
